import type { DisciplineInfo } from '@brighterly/lib-core-types/discipline/interfaces'
import type { Order, OrderItem } from '@brighterly/lib-core-types/orders/interfaces'
import type { Offer } from '@brighterly/lib-core-types/plans/interfaces'
import type { CheckoutStore } from './interfaces'
import type { ScopeStatus } from '@shared/lib/scope'
import type { CheckoutOrderEventProps } from './interfaces'

import { Discipline } from '@brighterly/lib-core-types/discipline/enums'
import { SubscriptionOption } from '@brighterly/lib-core-types/plans/enums'
import { DiscountMethod } from '@brighterly/lib-core-types/orders/enums'
import { OrderStatus, ProductCategory } from '@brighterly/lib-core-types/orders/enums'

import { useCommonStore } from '@shared/model'
import { isFullArray } from '@shared/lib/collections'
import { entryStatus } from '@shared/lib/scope'
import { toAnalyticsId } from '@shared/lib/amplitude'

import { isBillingComplete } from '../../lib/billing-completeness'
import { getOrder, getUpsellOrder } from './active-order'
import { oneTimePaymentGetters } from './one-time-payment'

const isBillingAddressComplete = (state: CheckoutStore): boolean => isBillingComplete(state.billingCountry, state.billingZipCode)

const isBusy = (state: CheckoutStore): boolean => state.loading || useCommonStore().isLoading

const isCheckoutSubscription = (state: CheckoutStore): boolean => typeof getOrder(state)?.subscription?.id === 'number'

const getOrderLoadStatus = (state: CheckoutStore): ScopeStatus => entryStatus(state.orderById, state.activeOrderId)
const getUpsellOrderLoadStatus = (state: CheckoutStore): ScopeStatus => entryStatus(state.upsellOrderById, state.activeOrderId)
const getOrderId = (state: CheckoutStore): number | null => getOrder(state)?.id || null
const getUpsellOrderId = (state: CheckoutStore): number | null => getUpsellOrder(state)?.id || null
const isOrderId = (state: CheckoutStore): boolean => getOrderLoadStatus(state) === 'ready'
const isUpsellOrderId = (state: CheckoutStore): boolean => getUpsellOrderLoadStatus(state) === 'ready'
const getOrderStatus = (state: CheckoutStore): OrderStatus | null => getOrder(state)?.status || null
const getUpsellOrderStatus = (state: CheckoutStore): OrderStatus | null => getUpsellOrder(state)?.status || null
const getTotal = (state: CheckoutStore): number => getOrder(state)?.total || 0
const getUpsellTotal = (state: CheckoutStore): number => getUpsellOrder(state)?.total || 0
const getSubscriptionTotal = (state: CheckoutStore): number => getOrder(state)?.subscription?.total || 0
const getDueNow = (state: CheckoutStore): number => getOrder(state)?.subscription?.first_payment || 0
const getRenewDate = (state: CheckoutStore): string => getOrder(state)?.subscription?.renew_date || ''
const getSubscriptionType = (state: CheckoutStore): SubscriptionOption | null => getOrder(state)?.subscription?.type || null
const getProductFromOrder = (order: Order | null): OrderItem['product'] | null => {
  const items = order?.items || []
  const filteredItems = items.filter(item => item.product.category !== ProductCategory.BONUS)
  return filteredItems?.[0]?.product || null
}
const getProduct = (state: CheckoutStore): OrderItem['product'] | null => getProductFromOrder(getOrder(state))
const getDiscountMethod = (state: CheckoutStore): DiscountMethod | null => {
  return isCheckoutSubscription(state)
    ? (getOrder(state)?.subscription?.discount_method || null)
    : (getOrder(state)?.discount_method || null)
}
const isDiscountMethodCustomerOffer = (state: CheckoutStore): boolean => (getDiscountMethod(state) === DiscountMethod.CustomerOffer)
const isDiscountMethodPromoCode = (state: CheckoutStore): boolean => (getDiscountMethod(state) === DiscountMethod.PromoCode)
const isDiscountMethodNone = (state: CheckoutStore): boolean => (getDiscountMethod(state) === DiscountMethod.None)
const getDiscount = (state: CheckoutStore): number => {
  const value = isCheckoutSubscription(state)
    ? getOrder(state)?.subscription?.discount
    : getOrder(state)?.discount
  return value || 0
}
const getDiscountUsd = (state: CheckoutStore): number => getOrder(state)?.discount_usd || 0
const getProductName = (state: CheckoutStore): string | null => getProduct(state)?.name || null
const getProductDiscipline = (state: CheckoutStore): Discipline => (getProduct(state)?.options?.lessons?.ela || 0) > 0 ? Discipline.ELA : Discipline.Math
const getCustomerOffer = (state: CheckoutStore): Offer | null => getOrder(state)?.customer_offer || null
const getOfferDiscount = (state: CheckoutStore): number => {
  return getCustomerOffer(state)?.discount || 0
}
const getQty = (state: CheckoutStore): number => {
  return getOrder(state)?.subscription?.parts || 0
}

/** The registry's option id: `monthly_<parts>` for a subscription order, `one_time` for a single payment. */
const getPaymentOptionId = (order: Order | null): string => {
  const parts = order?.subscription?.parts || 0

  return parts > 0 ? `monthly_${parts}` : 'one_time'
}

const getActiveChildOrder = (state: CheckoutStore): Order | null => {
  const orders: Order[] = getOrder(state)?.child_orders || []
  return orders.find(order => order.status !== OrderStatus.SUCCEEDED) || null
}

const getTaxAmount = (state: CheckoutStore): number => getOrder(state)?.tax_amount || 0

const getPricePerLesson = (state: CheckoutStore): number => getOrder(state)?.price_per_lesson || 0

const getDisciplineInfoFromOrder = (order: Order | null): DisciplineInfo | null => order?.disciplines?.[0] || null
const getDisciplineInfo = (state: CheckoutStore): DisciplineInfo | null => getDisciplineInfoFromOrder(getOrder(state))

/** Checkout context of every checkout / upsell event; the order's own discipline wins over the tracker's (spec D8). */
const getOrderEventProps = (order: Order | null): CheckoutOrderEventProps => {
  const discipline = getDisciplineInfoFromOrder(order)

  return {
    order_id: order?.id ?? null,
    product_name: getProductFromOrder(order)?.code ?? null,
    payment_option: getPaymentOptionId(order),
    order_amount: order?.subscription?.first_payment ?? order?.total ?? null,
    value: order?.upsell_placement ? toAnalyticsId(order.upsell_placement) : null,
    discipline_id: discipline?.id ?? null,
    discipline_code: discipline?.code ? toAnalyticsId(discipline.code) : null,
  }
}
const isAutoRenewal = (state: CheckoutStore): boolean => !!getOrder(state)?.subscription?.auto_renew

const getFakeBasePriceWithDiscount30 = (state: CheckoutStore): number => {
  const fakeDiscount = 30
  const total = getOrder(state)?.total || 0
  return total + (total * (fakeDiscount / 100))
}

export const getters = {
  isCheckoutSubscription: (state: CheckoutStore): boolean => isCheckoutSubscription(state),
  isLoading: (state: CheckoutStore): CheckoutStore['loading'] => state.loading,
  isBusy: (state: CheckoutStore): boolean => isBusy(state),
  isPaymentLocked: (state: CheckoutStore): boolean => state.paymentLocked || isBusy(state),
  isBlockPromoCode: (state: CheckoutStore): boolean => state.blockPromoCode,
  isOrder: (state: CheckoutStore): boolean => !!getOrder(state),
  getOrder: (state: CheckoutStore): Order | null => getOrder(state),
  getOrderLoadStatus: (state: CheckoutStore): ScopeStatus => getOrderLoadStatus(state),
  getUpsellOrderLoadStatus: (state: CheckoutStore): ScopeStatus => getUpsellOrderLoadStatus(state),
  getOrderId: (state: CheckoutStore): number | null => getOrderId(state),
  getUpsellOrderId: (state: CheckoutStore): number | null => getUpsellOrderId(state),
  getOrderStatus: (state: CheckoutStore): OrderStatus | null => getOrderStatus(state),
  isOrderProcessing: (state: CheckoutStore): boolean => getOrderStatus(state) === OrderStatus.PROCESSING,
  isOrderFailed: (state: CheckoutStore): boolean => getOrderStatus(state) === OrderStatus.FAILED,
  isOrderSucceeded: (state: CheckoutStore): boolean => getOrderStatus(state) === OrderStatus.SUCCEEDED,
  isOrderId: (state: CheckoutStore): boolean => isOrderId(state),
  isUpsellOrderId: (state: CheckoutStore): boolean => isUpsellOrderId(state),
  getUpsellOrderStatus: (state: CheckoutStore): OrderStatus | null => getUpsellOrderStatus(state),
  isUpsellOrderProcessing: (state: CheckoutStore): boolean => {
    const route = useRoute()

    return getUpsellOrderStatus(state) === OrderStatus.PROCESSING
      && route.query.order === 'finished'
  },
  isUpsellOrderFailed: (state: CheckoutStore): boolean => getUpsellOrderStatus(state) === OrderStatus.FAILED,
  isUpsellOrderSucceeded: (state: CheckoutStore): boolean => {
    const route = useRoute()

    return getUpsellOrderStatus(state) === OrderStatus.SUCCEEDED
      && route.query.order === 'finished'
  },
  getQty: (state: CheckoutStore): number => getQty(state),
  getProduct: (state: CheckoutStore): OrderItem['product'] | null => getProduct(state),
  getProductDiscipline: (state: CheckoutStore): Discipline => getProductDiscipline(state),
  isProduct: (state: CheckoutStore): boolean => !!getProduct(state),
  isDiscountMethodCustomerOffer: (state: CheckoutStore): boolean => isDiscountMethodCustomerOffer(state),
  isDiscountMethodPromoCode: (state: CheckoutStore): boolean => isDiscountMethodPromoCode(state),
  isDiscountMethodNone: (state: CheckoutStore): boolean => isDiscountMethodNone(state),
  getDiscount: (state: CheckoutStore): number => getDiscount(state),
  isDiscount: (state: CheckoutStore): boolean => !!getDiscount(state),
  getDiscountUsd: (state: CheckoutStore): number => getDiscountUsd(state),
  getProductName: (state: CheckoutStore): string | null => getProductName(state),
  getTotal: (state: CheckoutStore): number => getTotal(state),
  getUpsellTotal: (state: CheckoutStore): number => getUpsellTotal(state),
  getSubscriptionTotal: (state: CheckoutStore): number => getSubscriptionTotal(state),
  getDueNow: (state: CheckoutStore): number => getDueNow(state),
  getRenewDate: (state: CheckoutStore): string => getRenewDate(state),
  getSubscriptionType: (state: CheckoutStore): SubscriptionOption | null => getSubscriptionType(state),
  getCustomerOffer: (state: CheckoutStore): Offer | null => getCustomerOffer(state),
  getOfferDiscount: (state: CheckoutStore): number => getOfferDiscount(state),
  isOfferDiscount: (state: CheckoutStore): boolean => !!getOfferDiscount(state),
  isTypeFourWeeks: (state: CheckoutStore): boolean => getSubscriptionType(state) === SubscriptionOption.FourWeeks,
  isAppliesForPromocode: (state: CheckoutStore): CheckoutStore['appliesForPromocode'] => state.appliesForPromocode,
  getFurtherPayments: (state: CheckoutStore): CheckoutStore['furtherPayments'] => state.furtherPayments,
  isFurtherPayments: (state: CheckoutStore): boolean => isFullArray(state.furtherPayments),
  getPaymentData: (state: CheckoutStore): CheckoutStore['paymentData'] => state.paymentData,
  isPaymentData: (state: CheckoutStore): boolean => !!state.paymentData,
  getStripeClientSecret: (state: CheckoutStore): CheckoutStore['stripeClientSecret'] => state.stripeClientSecret,
  isStripeClientSecret: (state: CheckoutStore): boolean => !!state.stripeClientSecret,
  getPaypalScriptUrl: (state: CheckoutStore): CheckoutStore['paypalScriptUrl'] => state.paypalScriptUrl,
  isPaypalScriptUrl: (state: CheckoutStore): boolean => !!state.paypalScriptUrl,
  isModalFurtherPayments: (state: CheckoutStore): boolean => state.modalFurtherPayments,
  isPaypalScriptReady: (state: CheckoutStore): CheckoutStore['paypalScriptReady'] => state.paypalScriptReady,
  getActiveChildOrder: (state: CheckoutStore): Order | null => getActiveChildOrder(state),
  isActiveChildOrder: (state: CheckoutStore): boolean => !!getActiveChildOrder(state),
  getTaxAmount: (state: CheckoutStore): number => getTaxAmount(state),
  isTaxAmount: (state: CheckoutStore): boolean => getTaxAmount(state) > 0,
  getDisciplineInfo: (state: CheckoutStore): DisciplineInfo | null => getDisciplineInfo(state),
  getDisciplineCode: (state: CheckoutStore): Discipline | null => getDisciplineInfo(state)?.code || null,
  isMath: (state: CheckoutStore): boolean => getDisciplineInfo(state)?.code === Discipline.Math,
  isEla: (state: CheckoutStore): boolean => getDisciplineInfo(state)?.code === Discipline.ELA,
  getPricePerLesson: (state: CheckoutStore): number => getPricePerLesson(state),
  getFakeBasePriceWithDiscount30: (state: CheckoutStore): number => getFakeBasePriceWithDiscount30(state),
  isPaymentError: (state: CheckoutStore): CheckoutStore['paymentError'] => state.paymentError,
  getNotifyType: (state: CheckoutStore): CheckoutStore['notifyType'] => state.notifyType,
  isModalPaymentFailed: (state: CheckoutStore): CheckoutStore['modalPaymentFailed'] => state.modalPaymentFailed,
  getBillingCountry: (state: CheckoutStore): CheckoutStore['billingCountry'] => state.billingCountry,
  getBillingZipCode: (state: CheckoutStore): CheckoutStore['billingZipCode'] => state.billingZipCode,
  getBillingCountryState: (state: CheckoutStore): CheckoutStore['billingCountryState'] => state.billingCountryState,
  getBillingZipStatus: (state: CheckoutStore): CheckoutStore['billingZipStatus'] => state.billingZipStatus,
  isBillingConfirmed: (state: CheckoutStore): CheckoutStore['billingConfirmed'] => state.billingConfirmed,
  isBillingAddressComplete: (state: CheckoutStore): boolean => isBillingAddressComplete(state),
  isAutoRenewal: (state: CheckoutStore): boolean => isAutoRenewal(state),
  getEventProps: (state: CheckoutStore): CheckoutOrderEventProps => getOrderEventProps(getOrder(state)),
  getUpsellEventProps: (state: CheckoutStore): CheckoutOrderEventProps => getOrderEventProps(getUpsellOrder(state)),
  getParentOrderTotal: (state: CheckoutStore): number => {
    return getOrder(state)?.parent_order?.total || 0
  },
  getParentUpsellOrderTotal: (state: CheckoutStore): number => {
    return getUpsellOrder(state)?.parent_order?.total || 0
  },
  isModalFormSolidgate: (state: CheckoutStore): CheckoutStore['modalFormSolidgate'] => state.modalFormSolidgate,
  isModalCurrentMethodPayment: (state: CheckoutStore): CheckoutStore['modalCurrentMethodPayment'] => state.modalCurrentMethodPayment,
  ...oneTimePaymentGetters,
}
